import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    AiEnhancedAssetType,
    InsightOutputFormat,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isUndefined } from "lodash";
import * as React from "react";
import { useState } from "react";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import type { DownloadAsset } from "../../types/common";
import { relevantInsightTypeLookup } from "../../utils/aiEnhancedInsights.utils";
import { isLoading } from "../../utils/api.utils";
import { getPhoenixContentInfoFromStore } from "../../utils/phoenix.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { AiTextIcon } from "../AiTextIcon";

import { RelevantInsightSheet } from "./RelevantInsightSheet";

export interface RelevantInsightButtonProps {
    previewAsset?: DownloadAsset;
    downloadAsset?: DownloadAsset;
    sourceInsightType: InsightType;
    sourceIdentifier: string;
    requesterAlias: string;
    advertiserId: string;
}

const NOT_FOUND_MESSAGE = "No suggested content found.";

export const RelevantInsightButton: React.FunctionComponent<
    RelevantInsightButtonProps
> = (props) => {
    const {
        sourceIdentifier,
        requesterAlias,
        sourceInsightType,
        advertiserId,
    } = props;

    const [isSheetOpen, setSheetOpen] = useState(false);

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const identifier: string = `relevant-${sourceIdentifier}`;
    const insightType: InsightType =
        relevantInsightTypeLookup[sourceInsightType];

    const [downloadState, downloadAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getGeneratedInsightDownload({
            requesterAlias,
            executionId: sourceIdentifier,
            advertiserId,
            aiEnhancedAssetType: AiEnhancedAssetType.RELEVANT_INSIGHTS_DECK,
        });
        setDownloadAsset(data as DownloadAsset);
    });
    useAsyncErrorStatusNotification(
        downloadState,
        [],
        { NotFoundException: NOT_FOUND_MESSAGE },
        identifier,
    );

    const [previewState, previewAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getGeneratedInsightDownload({
            requesterAlias,
            executionId: sourceIdentifier,
            advertiserId,
            aiEnhancedAssetType: AiEnhancedAssetType.RELEVANT_INSIGHTS_DECK,
            outputFormat: InsightOutputFormat.PDF,
        });
        setPreviewAsset(data as DownloadAsset);
    });
    useAsyncErrorStatusNotification(
        previewState,
        [],
        { NotFoundException: NOT_FOUND_MESSAGE },
        identifier,
    );

    const handleSheetOpen = () => {
        logWithTakt(TELEMETRY_ID.VIEW_G4S_RELEVANT_INSIGHTS_CLICKED, {
            identifier,
            insightType,
            sourceIdentifier,
            sourceInsightType,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.VIEW_G4S_RELEVANT_INSIGHTS_CLICKED,
            PHOENIX_FEATURE.AI,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    sourceInsightType,
                    sourceIdentifier,
                ),
            },
        );
        void previewAction.execute();
        void downloadAction.execute();
    };

    React.useEffect(() => {
        if (!isUndefined(previewAsset) && !isUndefined(downloadAsset)) {
            setSheetOpen(true);
        }
    }, [previewAsset, downloadAsset]);

    const loading =
        isLoading(previewState.status) || isLoading(downloadState.status);

    return (
        <>
            <RelevantInsightSheet
                identifier={identifier}
                insightType={insightType}
                previewAsset={previewAsset}
                downloadAsset={downloadAsset}
                isSheetOpen={isSheetOpen}
                setSheetOpen={setSheetOpen}
            />
            <TextButton
                data-testid={`relevant-insight-button-${sourceIdentifier}`}
                onClick={handleSheetOpen}
                disabled={loading}
            >
                <AiTextIcon
                    text="Generate suggested content"
                    loadingText="Generating suggested content"
                    loading={loading}
                />
            </TextButton>
        </>
    );
};
